import { useEffect, useState } from "react";
import { DeviceMobile, X, Share, Plus, DotsThreeVertical } from "@phosphor-icons/react";

const DISMISSED_KEY = "medeasy_pwa_dismissed_until";
const DISMISS_DAYS = 7;

function detectPlatform() {
  if (typeof window === "undefined") return { ios: false, android: false, supported: false };
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  const isAndroid = /Android/i.test(ua);
  // iOS Safari supports add-to-home-screen via Share. Android Chrome supports
  // it via the kebab menu — most Chromium-based Android browsers also work.
  return {
    ios: isIOS && isSafari,
    android: isAndroid,
    supported: (isIOS && isSafari) || isAndroid,
  };
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/**
 * "Install to home screen" hint for iOS Safari and Android browsers.
 * Shows once per 7 days unless dismissed. Hidden when already installed (standalone).
 */
export default function InstallHint() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState({ ios: false, android: false });

  useEffect(() => {
    if (isStandalone()) return;
    const p = detectPlatform();
    if (!p.supported) return;
    setPlatform(p);
    const until = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
    if (until > Date.now()) return;
    const t = setTimeout(() => setVisible(true), 1800);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000));
  };

  if (!visible) return null;

  const deviceLabel = platform.android ? "Android phone" : "iPhone";

  return (
    <div
      data-testid="install-hint-banner"
      className="fixed bottom-4 left-4 right-4 z-[100] rounded-2xl bg-white border border-[#E3E1D9] shadow-[0_10px_40px_rgba(28,46,33,0.20)] p-4 flex items-start gap-3 max-w-md mx-auto"
      role="dialog"
      aria-label={`Install MedEasy on your ${deviceLabel}`}
    >
      <div className="shrink-0 w-11 h-11 rounded-xl bg-[#F7F6F2] flex items-center justify-center overflow-hidden ring-1 ring-[#E3E1D9]">
        <img src="/icon-192.png" alt="MedEasy" className="w-10 h-10 object-contain" />
      </div>
      <div className="flex-1 text-sm leading-snug">
        <div className="font-[Manrope] font-semibold text-[#1E2420]">Install MedEasy on your {deviceLabel}</div>
        <div className="text-[#5C6660] mt-0.5 inline-flex items-center gap-1 flex-wrap">
          {platform.android ? (
            <>
              Tap <DotsThreeVertical size={14} weight="bold" className="inline" /> then <strong>&ldquo;Install app&rdquo;</strong> or <strong>&ldquo;Add to Home screen&rdquo;</strong>
            </>
          ) : (
            <>
              Tap <Share size={14} weight="bold" className="inline" /> then <strong className="inline-flex items-center gap-0.5">&ldquo;Add to Home Screen&rdquo;<Plus size={12} weight="bold" /></strong>
            </>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="install-hint-dismiss"
        className="shrink-0 text-[#5C6660] hover:text-[#1E2420] p-1 -m-1"
      >
        <X size={18} weight="bold" />
      </button>
    </div>
  );
}

// Re-exporting DeviceMobile to avoid lint-unused if we expand later
export { DeviceMobile };
